export const PAYLOAD_PRODUCT_ID_TOKEN = '[payloadProductId]';
export const FORWARD_PARAMETERS_TOKEN = '[forwardParameters]';
export const PAYLOAD_CRITERIA_TOKEN = '[payloadCriteria]';

const CATALOGUE_VERSION_PARAM = 'catalogue-version';
const CATALOGUE_VERSION_VALUE = '2';
const CATALOGUE_VERSION_QUERY_REGEX = /[?&]catalogue-version=/;

/**
 * Substitutes legacy HubSpot `react-cms` link tokens carried by Strapi-authored CMS URLs.
 *
 * - `[payloadProductId]` → replaced with the concrete `productId`. Appends
 *   `catalogue-version=2` to the URL's query string to keep "always V2" parity
 *   with HubSpot, but only when the query string does not already contain it —
 *   CMS-1679's `buildTravelInsuranceQueryString` already emits that param for
 *   TI verticals, so unconditional appending would duplicate it. Works for
 *   tokens in both query position (`?productId=[payloadProductId]`) and path
 *   position (`/products/[payloadProductId]/apply`).
 * - `[forwardParameters]` → spliced with `currentSearch`, after renaming `tripType`
 *   to `insuranceType`. Only substituted when `currentSearch` is explicitly provided
 *   (client-side context). On the RSC path callers pass `undefined` and the token is
 *   left in place — a literal token reaching a provider URL fails loudly in QA
 *   rather than silently dropping CMS author intent.
 *
 * Pure / no `window` reads — safe for SSR.
 */
export const substituteCmsUrlTokens = (
  url: string,
  options: { productId?: string; currentSearch?: string, payloadCriteria?: string } = {},
): string => {
  const { productId, currentSearch, payloadCriteria } = options;

  let result = url;

  if (productId && result.includes(PAYLOAD_PRODUCT_ID_TOKEN)) {
    // Split off the fragment up-front. Both the catalogue-version guard and
    // the append must operate on the pre-fragment portion: a `catalogue-version=`
    // inside the hash must not suppress the append, and the appended param must
    // land in the query string, not the hash.
    const [beforeFragment, fragment] = result.split('#');
    const needsCatalogueVersion =
      !CATALOGUE_VERSION_QUERY_REGEX.test(beforeFragment);
    let withSubstitution = beforeFragment.replace(
      PAYLOAD_PRODUCT_ID_TOKEN,
      productId,
    );
    if (needsCatalogueVersion) {
      const separator = withSubstitution.includes('?') ? '&' : '?';
      withSubstitution = `${withSubstitution}${separator}${CATALOGUE_VERSION_PARAM}=${CATALOGUE_VERSION_VALUE}`;
    }
    result =
      fragment !== undefined
        ? `${withSubstitution}#${fragment}`
        : withSubstitution;
  }

  // `currentSearch === undefined` distinguishes the RSC path (caller cannot supply)
  // from the client path with no params (empty string still triggers substitution).
  if (
    currentSearch !== undefined &&
    result.includes(FORWARD_PARAMETERS_TOKEN)
  ) {
    const params = new URLSearchParams(currentSearch);
    const tripType = params.get('tripType');
    if (tripType) {
      // `set` (not `append`) — avoid duplicate `insuranceType` when the caller's
      // search already contains both `tripType` and `insuranceType`.
      params.set('insuranceType', tripType);
      params.delete('tripType');
    }
    result = result.replace(FORWARD_PARAMETERS_TOKEN, params.toString());
  }

  // TODO: HACK to rapid fix, use marker replacement like [payloadCriteria]
  if (payloadCriteria && result.includes('criteria=[payloadCriteria]')) {
    result = result.replace('criteria=[payloadCriteria]', payloadCriteria);
  } else if (payloadCriteria && result.includes('criteria=%5BpayloadCriteria%5D')) {
    result = result.replace('criteria=%5BpayloadCriteria%5D', payloadCriteria);
  }

  // Collapse duplicate query-string keys that upstream merges/splices can
  // introduce (e.g. productId / catalogue-version emitted twice). Kept
  // deliberately narrow:
  // - Skipped while an unsubstituted token remains (literal `[...]` or its
  //   percent-encoded `%5B...%5D` form) — those URLs are resolved again
  //   downstream (client-side) and a URLSearchParams round-trip here would
  //   percent-encode the token brackets and break that later pass.
  // - Only the query string is rewritten; the fragment (which may itself carry
  //   `?...`) is split off first and re-attached verbatim.
  // - Rewrites only when a real duplicate is found, so non-duplicate URLs pass
  //   through byte-for-byte.
  // Match both the literal token (`[payloadProductId]`) and the percent-encoded
  // form (`%5BpayloadProductId%5D`) that an upstream `new URL()` / URLSearchParams
  // round-trip can produce — either means the URL still needs a downstream
  // resolution pass and must not be normalized here.
  const encodesToken = (token: string): boolean =>
    result.includes(token) ||
    result.includes(token.replace('[', '%5B').replace(']', '%5D'));

  const hasUnresolvedToken =
    encodesToken(PAYLOAD_PRODUCT_ID_TOKEN) ||
    encodesToken(FORWARD_PARAMETERS_TOKEN) ||
    encodesToken(PAYLOAD_CRITERIA_TOKEN);

  if (!hasUnresolvedToken) {
    const hashIndex = result.indexOf('#');
    const beforeFragment =
      hashIndex === -1 ? result : result.slice(0, hashIndex);
    const fragment = hashIndex === -1 ? '' : result.slice(hashIndex);

    const queryIndex = beforeFragment.indexOf('?');
    if (queryIndex !== -1) {
      const pathPart = beforeFragment.slice(0, queryIndex);
      const queryPart = beforeFragment.slice(queryIndex + 1);

      const deduped = new URLSearchParams();
      let hadDuplicate = false;
      for (const [key, value] of new URLSearchParams(queryPart).entries()) {
        if (deduped.has(key)) {
          hadDuplicate = true;
          continue;
        }
        deduped.append(key, value);
      }

      if (hadDuplicate) {
        result = `${pathPart}?${deduped.toString()}${fragment}`;
      }
    }
  }

  return result;
};
